<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import { useAudiencesStore } from 'dashboard/stores/audiences';

import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AudienceFormDialog from 'dashboard/components-next/Audiences/AudienceFormDialog.vue';
import AudienceContactsDialog from 'dashboard/components-next/Audiences/AudienceContactsDialog.vue';

const { t } = useI18n();
const audiencesStore = useAudiencesStore();

const records = computed(() => audiencesStore.getAudiencesList);
const uiFlags = computed(() => audiencesStore.uiFlags);

const tableHeaders = computed(() => [
  t('AUDIENCES.LIST.COLUMNS.NAME'),
  t('AUDIENCES.LIST.COLUMNS.STATUS'),
  t('AUDIENCES.LIST.COLUMNS.CREATED_AT'),
  t('AUDIENCES.LIST.COLUMNS.ACTIONS'),
]);

onMounted(() => audiencesStore.get());

const contactsDialogRef = ref(null);

const formDialogRef = ref(null);
const editingAudience = ref(null);

const openCreateDialog = () => {
  editingAudience.value = null;
  formDialogRef.value?.dialogRef.open();
};

const openEditDialog = audience => {
  editingAudience.value = audience;
  formDialogRef.value?.dialogRef.open();
};

const handleSave = async ({ id, name, criteria }) => {
  try {
    if (id) {
      await audiencesStore.update({ id, name, criteria });
    } else {
      await audiencesStore.create({ name, criteria });
    }
    useAlert(t('AUDIENCES.FORM.SAVE_SUCCESS'));
    formDialogRef.value?.dialogRef.close();
  } catch (error) {
    useAlert(error.message || t('AUDIENCES.FORM.SAVE_ERROR'));
  }
};

const deleteDialogRef = ref(null);
const audiencePendingDelete = ref(null);

const openDeleteDialog = audience => {
  audiencePendingDelete.value = audience;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await audiencesStore.delete(audiencePendingDelete.value.id);
    useAlert(t('AUDIENCES.DELETE_DIALOG.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUDIENCES.DELETE_DIALOG.ERROR_MESSAGE'));
  } finally {
    deleteDialogRef.value?.close();
  }
};

// Audience#created_at is serialized as an ISO 8601 string (jbuilder's
// default for a Time value), not Unix seconds like some other endpoints.
const { dateOnly } = useLocalizedTime();
const formatDate = value =>
  value ? dateOnly(new Date(value).getTime() / 1000) : '';
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div
        class="flex items-center justify-between w-full max-w-5xl mx-auto h-20"
      >
        <span class="text-heading-1 text-n-slate-12">
          {{ t('AUDIENCES.LIST.HEADER_TITLE') }}
        </span>
        <Button
          :label="t('AUDIENCES.LIST.CREATE_BUTTON')"
          color="blue"
          @click="openCreateDialog"
        />
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl py-4 mx-auto">
        <div
          v-if="uiFlags.fetchingList"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <BaseTable
          v-else
          :headers="tableHeaders"
          :items="records"
          :no-data-message="t('AUDIENCES.LIST.EMPTY_STATE')"
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="audience in items"
              :key="audience.id"
              :item="audience"
              class="cursor-pointer hover:bg-n-alpha-1"
              @click="contactsDialogRef.open(audience)"
            >
              <template #default>
                <BaseTableCell>
                  <span class="text-body-main text-n-slate-12 hover:underline">
                    {{ audience.name }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    v-if="audience.stale"
                    class="rounded-full px-2 py-0.5 text-xs font-medium text-n-amber-11 bg-n-amber-3"
                  >
                    {{ t('AUDIENCES.LIST.STALE_BADGE') }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{ formatDate(audience.createdAt) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <div class="flex gap-2" @click.stop>
                    <Button
                      :label="t('AUDIENCES.LIST.ACTIONS.EDIT')"
                      slate
                      faded
                      sm
                      @click="openEditDialog(audience)"
                    />
                    <Button
                      :label="t('AUDIENCES.LIST.ACTIONS.DELETE')"
                      ruby
                      faded
                      sm
                      @click="openDeleteDialog(audience)"
                    />
                  </div>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>
    </main>

    <AudienceFormDialog
      ref="formDialogRef"
      :audience-data="editingAudience"
      :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
      @save="handleSave"
    />

    <AudienceContactsDialog ref="contactsDialogRef" />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('AUDIENCES.DELETE_DIALOG.TITLE')"
      :description="
        t('AUDIENCES.DELETE_DIALOG.DESCRIPTION', {
          audienceName: audiencePendingDelete?.name,
        })
      "
      :confirm-button-label="t('AUDIENCES.DELETE_DIALOG.CONFIRM')"
      :is-loading="uiFlags.deletingItem"
      @confirm="confirmDelete"
    />
  </section>
</template>
